import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Crown, Sparkles, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';
import LanguagePicker from '@/components/LanguagePicker';
import RetrievalModeToggle from '@/components/RetrievalModeToggle';

/**
 * PremiumSectionDialog — admin-only. Renders a section-type picker (Abstract /
 * Introduction / Methods / Results / Discussion / ...) with per-section
 * expert-written prompts and heavier RAG settings (20 chunks, 3500 word floor).
 */
export default function PremiumSectionDialog({
  open, onOpenChange,
  userId, projectId, language: defaultLanguage,
  onGenerated,
}) {
  const [sections, setSections] = useState([]);
  const [sectionKey, setSectionKey] = useState('introduction');
  const [authorNotes, setAuthorNotes] = useState('');
  const [language, setLanguage] = useState(defaultLanguage || 'en');
  const [retrievalMode, setRetrievalMode] = useState('hybrid');
  const [minWords, setMinWords] = useState(3500);
  const [nChunks, setNChunks] = useState(20);
  const [engine, setEngine] = useState('multi'); // InkPhD Deep Writer by default
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    thesisApi.listPremiumSectionTypes(userId)
      .then((d) => setSections(d.sections || []))
      .catch(() => toast.error('Could not load premium section list.'));
  }, [open, userId]);

  useEffect(() => { setLanguage(defaultLanguage || 'en'); }, [defaultLanguage]);

  const estimateMinutes = engine === 'multi'
    ? Math.max(5, Math.round(minWords / 350))   // 3-pass is slower
    : Math.max(2, Math.round(minWords / 800));

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    try {
      const placeholder = await thesisApi.premiumGenerateSection(userId, {
        project_id: projectId,
        section_key: sectionKey,
        user_prompt: authorNotes,
        language,
        n_chunks: nChunks,
        min_words: minWords,
        retrieval_mode: retrievalMode,
        engine,
      });
      // The endpoint now returns a placeholder chapter with premium_status='running'.
      // We close the dialog immediately and let the parent poll via /chapters/{id}.
      toast.success(
        `Premium generation started — ${engine === 'multi' ? 'multi-pass (5 generations)' : 'single-pass (2 generations)'} (${estimateMinutes}-${estimateMinutes + 3} min). Watch the chapter in Writing tab; it will update when ready.`,
        { duration: 6000 },
      );
      onGenerated?.(placeholder);
      onOpenChange(false);
    } catch (e) {
      toast.error('Could not start premium generation: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  }, [userId, projectId, sectionKey, authorNotes, language, nChunks, minWords, retrievalMode, engine, onGenerated, onOpenChange, estimateMinutes]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="premium-section-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="p-1.5 rounded-md bg-gradient-to-br from-amber-500/20 to-amber-700/20 border border-amber-600/30">
              <Crown className="w-4 h-4 text-amber-700 dark:text-amber-400" />
            </div>
            <span className="text-[10px] uppercase tracking-widest font-bold text-amber-700 dark:text-amber-400">Premium · Researcher plan and above</span>
          </div>
          <DialogTitle className="font-serif text-2xl" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
            Premium section writer
          </DialogTitle>
          <DialogDescription>
            Expert-written prompt templates for each canonical section. Heavier RAG retrieval (default 20 chunks),
            longer minimum length (3,500 words), strict hallucination guardrails.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Section picker */}
          <div>
            <label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2 block">Section type</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5" data-testid="premium-section-picker">
              {sections.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSectionKey(s.key)}
                  className={`px-2.5 py-2 rounded-md text-xs font-medium transition-all text-left ${
                    sectionKey === s.key
                      ? 'bg-[#0b1a35] text-white shadow-sm'
                      : 'bg-muted/40 text-foreground hover:bg-muted'
                  }`}
                  data-testid={`premium-section-${s.key}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Author notes */}
          <div>
            <label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-1 block">Author notes (optional)</label>
            <Textarea
              value={authorNotes}
              onChange={(e) => setAuthorNotes(e.target.value)}
              rows={3}
              placeholder="Any specific focus, e.g. 'Emphasise endoscopic vs surgical approach in necrotising pancreatitis. Include 2019 WSES guidelines.'"
              data-testid="premium-author-notes"
            />
          </div>

          {/* Engine toggle: InkPhD Deep Writer (multi-pass) vs Fast Writer (single-pass) */}
          <div className="rounded-md border border-amber-600/30 bg-amber-500/5 p-3">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-700 dark:text-amber-400 mb-2">
              How deep should this section go?
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setEngine('multi')}
                className={`text-left p-2.5 rounded-md border transition-all ${
                  engine === 'multi'
                    ? 'border-amber-600 bg-amber-500/10 ring-1 ring-amber-600/40'
                    : 'border-border bg-background hover:bg-muted/40'
                }`}
                data-testid="premium-engine-multi"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-xs font-semibold">
                    <Crown className="w-3.5 h-3.5 text-amber-600" />
                    InkPhD Deep Writer
                  </span>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-600/15 text-amber-700 dark:text-amber-400"
                    data-testid="premium-engine-multi-cost">5 generations</span>
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  3 passes: multi-perspective outline → per-subsection draft → claim-by-claim
                  verification. Every paragraph is audited against your library. 6-15 min.
                </div>
              </button>
              <button
                type="button"
                onClick={() => setEngine('single')}
                className={`text-left p-2.5 rounded-md border transition-all ${
                  engine === 'single'
                    ? 'border-[#1d4ed8] bg-[#1d4ed8]/5 ring-1 ring-[#1d4ed8]/40'
                    : 'border-border bg-background hover:bg-muted/40'
                }`}
                data-testid="premium-engine-single"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-xs font-semibold">
                    <Sparkles className="w-3.5 h-3.5 text-[#1d4ed8]" />
                    InkPhD Fast Writer
                  </span>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#1d4ed8]/15 text-[#1d4ed8] dark:text-[#7aa9ff]"
                    data-testid="premium-engine-single-cost">2 generations</span>
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  One GPT-5.2 call with the expert per-section prompt and the same wide retrieval.
                  No verification pass. 3-6 min. Best for first drafts and quick iterations.
                </div>
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2" data-testid="premium-engine-method-note">
              Method note: the Deep Writer implements a multi-perspective outline stage (after
              STORM, Stanford) and a self-verification stage (after OpenScholar, AI2), built on
              InkPhD&apos;s own hybrid retrieval. Not affiliated with either project.
            </p>
          </div>

          {/* Min words + chunks */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium block mb-1">Min length: <span className="text-[#1d4ed8] dark:text-[#7aa9ff] font-semibold">{minWords.toLocaleString()} w</span></label>
              <input type="range" min={2000} max={8000} step={500} value={minWords}
                onChange={(e) => setMinWords(parseInt(e.target.value, 10))}
                className="w-full" data-testid="premium-minwords" />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Evidence chunks: <span className="text-[#1d4ed8] dark:text-[#7aa9ff] font-semibold">{nChunks}</span></label>
              <input type="range" min={8} max={32} step={2} value={nChunks}
                onChange={(e) => setNChunks(parseInt(e.target.value, 10))}
                className="w-full" data-testid="premium-nchunks" />
            </div>
          </div>

          {/* Language + retrieval mode */}
          <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-md bg-muted/40 border border-border">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Lang</span>
              <LanguagePicker value={language} onChange={setLanguage} testid="premium-language-picker" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Retrieval</span>
              <RetrievalModeToggle value={retrievalMode} onChange={setRetrievalMode} testid="premium-retrieval" />
            </div>
          </div>

          {/* Disclosure */}
          <div className="text-[11px] text-muted-foreground flex items-start gap-2 p-2.5 rounded-md bg-amber-500/5 border border-amber-600/20">
            <Shield className="w-3.5 h-3.5 mt-px shrink-0 text-amber-700 dark:text-amber-400" />
            <p>Premium uses a per-section expert prompt with strict citation-only-from-library rules. Generation typically takes <strong>{estimateMinutes}-{estimateMinutes + 3} minutes</strong> for {minWords.toLocaleString()} words.</p>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={generating} data-testid="premium-cancel">
              Cancel
            </Button>
            <Button onClick={handleGenerate} disabled={generating}
              className="bg-gradient-to-r from-amber-700 to-amber-600 hover:from-amber-800 hover:to-amber-700 text-white"
              data-testid="premium-generate-btn">
              {generating
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating ({estimateMinutes}-{estimateMinutes + 3} min)...</>
                : <><Sparkles className="w-4 h-4 mr-2" /> Generate section</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
